import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBaseUrl } from "../config/api";
import LookingForJobCard from "./shared/LookingForJobCard";
import LookingForJobForm from "./LookingForJobForm";
import { canApplyForJobs } from "../utils/userRole";

const API_URL = getApiBaseUrl();

/**
 * Full list of open-to-work / looking-for-job posts.
 */
export default function LookingForJobBoard({ user }) {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/get_looking_for_job_posts.php?limit=50`);
      const data = await res.json();
      if (data.success) {
        setPosts(data.posts || []);
      } else {
        setPosts([]);
      }
    } catch (err) {
      console.error("Looking-for-job board load error:", err);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = query.trim().toLowerCase();
  const filtered = !q
    ? posts
    : posts.filter((p) => {
        const skills = Array.isArray(p.looking_for_job_skills)
          ? p.looking_for_job_skills.join(" ")
          : "";
        const hay = [
          p.title,
          p.looking_for_job_location,
          p.looking_for_job_availability,
          p.looking_for_job_description,
          p.text,
          p.username,
          p.first_name,
          p.last_name,
          skills,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      });

  return (
    <div
      style={{
        maxWidth: 860,
        margin: "0 auto",
        padding: "24px 16px 64px",
        minHeight: "100vh",
        background: "#f8fafc",
      }}
    >
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={{
          border: "none",
          background: "transparent",
          color: "#1d4ed8",
          cursor: "pointer",
          fontSize: 14,
          marginBottom: 12,
          padding: 0,
          fontWeight: 600,
        }}
      >
        ← Back
      </button>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 20,
        }}
      >
        <div>
          <h1
            style={{
              margin: "0 0 6px",
              fontSize: 28,
              fontWeight: 800,
              color: "#0f172a",
            }}
          >
            Open to work
          </h1>
          <p style={{ margin: 0, fontSize: 14, color: "#64748b" }}>
            Browse everyone looking for a job or internship.
          </p>
        </div>

        {canApplyForJobs(user) && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            style={{
              padding: "10px 16px",
              borderRadius: 10,
              border: "none",
              background: "#1d4ed8",
              color: "#fff",
              fontWeight: 650,
              fontSize: 14,
              cursor: "pointer",
            }}
          >
            Post open to work
          </button>
        )}
      </div>

      <input
        type="search"
        placeholder="Search by role, skills, location, name…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "12px 14px",
          borderRadius: 12,
          border: "1px solid #cbd5e1",
          marginBottom: 16,
          fontSize: 14,
          outline: "none",
          background: "#fff",
        }}
      />

      {loading ? (
        <p style={{ textAlign: "center", color: "#64748b", padding: 40 }}>
          Loading posts…
        </p>
      ) : filtered.length === 0 ? (
        <div
          style={{
            background: "#fff",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            padding: 32,
            textAlign: "center",
            color: "#64748b",
          }}
        >
          {posts.length === 0
            ? "No open-to-work posts yet."
            : "No posts match your search."}
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {filtered.map((post) => (
            <div
              key={post.id}
              style={{
                background: "#fff",
                borderRadius: 16,
                border: "1px solid #e2e8f0",
                padding: 16,
                boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
              }}
            >
              <LookingForJobCard post={post} />
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <LookingForJobForm
          user={user}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            setShowForm(false);
            load();
          }}
          onRefresh={load}
        />
      )}
    </div>
  );
}
